/**
 * MapReportPopup.js
 * ---------------------------------------------------------------------
 * Floating "report window" opened by clicking the MapReportPreview panel
 * over the map. Shows the same report views used by ReportPage, in a
 * compact scaled-down window, plus a footer to download the report as a
 * PDF (same flow as ReportPage) or jump to the full Report page.
 */
import React, { useState, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ConstituencyReportView from "./ConstituencyReportView";
import BusinessReportView from "./BusinessReportView";
import HolidayTaxReportView from "./HolidayTaxReportView";
import MotabilityReportView from "./MotabilityReportView";

// The report views render at fixed A4 dimensions; we scale them down so the
// whole page fits inside a compact window over the map.
const REPORT_W = 794;
const REPORT_H = 1123;
const SCALE = 0.62;

// Per-type label shown in the window header.
const REPORT_TYPE_LABELS = {
  default: "Political",
  business: "Business",
  client: "Holiday Tax",
  motability: "Motability",
};

// Wait for any <img> inside the report to finish loading before rasterising
// (mirrors ReportPage.waitForImages).
async function waitForImages(rootEl) {
  const imgs = Array.from(rootEl.querySelectorAll("img"));
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return;
      return new Promise((res) => {
        img.addEventListener("load", res, { once: true });
        img.addEventListener("error", res, { once: true });
      });
    })
  );
}

export default function MapReportPopup({
  open,
  areaCode,
  constName,
  reportType = "default",
  extDataBlurb,
  demographics,
  mapImages,
  isDemoUser = false,
  onClose,
  onSeeMoreReports,
}) {
  const [generating, setGenerating] = useState(false);
  const reportRef = useRef(null);
  const zoomRef = useRef(null);
  const bodyRef = useRef(null);

  if (!open || !areaCode) return null;

  const renderReport = () => {
    switch (reportType) {
      case "business":
        return <BusinessReportView constName={constName} areaCode={areaCode} />;
      case "client":
        return (
          <HolidayTaxReportView
            constName={constName}
            areaCode={areaCode}
            majority={extDataBlurb?.majority}
          />
        );
      case "motability":
        return <MotabilityReportView constName={constName} areaCode={areaCode} />;
      // "default" → the political report. Also the fallback for any
      // report type without a dedicated view yet.
      case "default":
      default:
        return (
          <ConstituencyReportView
            constName={constName}
            areaCode={areaCode}
            mpName={extDataBlurb?.mp_name}
            party={extDataBlurb?.mp_party}
            roles={extDataBlurb?.mp_roles}
            majority={extDataBlurb?.majority}
            demographics={demographics}
            mapImages={mapImages}
          />
        );
    }
  };

  // Rasterise the (unscaled) report and save a multi-page A4 PDF — same
  // approach as ReportPage.generatePdfFor, but targeting our ref so the
  // scaled thumbnail elsewhere on the page can't be picked up by mistake.
  const handleDownload = async () => {
    const element = reportRef.current;
    if (!element || generating) return;
    setGenerating(true);
    // Let the "Preparing…" overlay paint, then capture at natural size.
    // html2canvas mislays text when an ancestor uses CSS `zoom`, so reset the
    // wrapper's zoom to 1 for the capture (hidden by the overlay) and restore.
    await new Promise((r) => setTimeout(r, 60));
    const wrapper = zoomRef.current;
    const body = bodyRef.current;
    // Freeze the body at its current (scaled) size and clip, so resetting the
    // report to full size for capture doesn't grow the dialog.
    if (body) {
      const rect = body.getBoundingClientRect();
      body.style.width = `${rect.width}px`;
      body.style.height = `${rect.height}px`;
      body.style.overflow = "hidden";
    }
    if (wrapper) wrapper.style.zoom = "1";
    try {
      await waitForImages(element);
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "pt", "a4");
      const imgWidth = 595;
      const pageHeight = 842;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const safeName = String(constName || "report").replace(/\s+/g, "_");
      const fileName =
        reportType === "business"
          ? `ConstituencyProfile_Business_${safeName}.pdf`
          : `ConstituencyProfile_${safeName}.pdf`;
      pdf.save(fileName);
    } catch (e) {
      console.error("[MapReportPopup] PDF generation failed:", e);
    } finally {
      // Restore the on-screen scale (falls back to the sx `zoom: SCALE` class)
      // and release the frozen body dimensions.
      if (wrapper) wrapper.style.zoom = "";
      if (body) {
        body.style.width = "";
        body.style.height = "";
        body.style.overflow = "";
      }
      setGenerating(false);
    }
  };

  const typeLabel = REPORT_TYPE_LABELS[reportType] || REPORT_TYPE_LABELS.default;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          m: 2,
          maxHeight: "92vh",
          position: "relative",
        },
      }}
    >
      {/* Covers the window during PDF capture (report briefly reflows to full
          size at zoom 1 for html2canvas — the overlay hides that). */}
      {generating && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            bgcolor: "rgba(255,255,255,0.94)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress size={26} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
            Preparing your PDF…
          </Typography>
        </Box>
      )}
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          borderBottom: "0.5px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ maxWidth: 320 }}>
            {constName || "Constituency"}
          </Typography>
          <Chip
            label={typeLabel}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              bgcolor: "#e6f1fb",
              color: "#185fa5",
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label="Close report">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Report body ──
          Scaled with `zoom` (not `transform: scale`): zoom reflows the layout
          so text renders crisply at the smaller size. A fractional CSS
          transform on this large text-heavy report leaves ghosted/overlapping
          glyphs in Chromium's compositor. reportRef sits on the unscaled
          report so the PDF is captured at full resolution. */}
      <Box ref={bodyRef} sx={{ overflow: "auto", bgcolor: "grey.100", p: 1.5, display: "flex", justifyContent: "center" }}>
        <Box ref={zoomRef} sx={{ zoom: SCALE }}>
          <Box ref={reportRef} sx={{ width: REPORT_W, height: REPORT_H, bgcolor: "#fff" }}>
            {renderReport()}
          </Box>
        </Box>
      </Box>

      {/* ── Footer: navigate to Report page + download ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.25,
          borderTop: "0.5px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Button
          variant="text"
          onClick={onSeeMoreReports}
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{ fontSize: 13, fontWeight: 600, color: "#185fa5", textTransform: "none" }}
        >
          See more reports here
        </Button>

        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={isDemoUser || generating}
          startIcon={
            generating ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />
          }
          sx={{
            borderRadius: 2,
            textTransform: "none",
            bgcolor: "#111827",
            "&:hover": { bgcolor: "#0f172a" },
            ...(isDemoUser
              ? { bgcolor: "#9ca3af", color: "#f3f4f6", "&:hover": { bgcolor: "#9ca3af" } }
              : {}),
          }}
        >
          {isDemoUser ? "Download in full access" : generating ? "Preparing…" : "Download PDF"}
        </Button>
      </Box>
    </Dialog>
  );
}
